import React, { useCallback, useMemo, useState } from "react";
import DatePickerCmp from "react-datepicker";
import { createClasses, InputField } from "@bloomberg/media-ui-core";
import "react-datepicker/dist/react-datepicker.css";
import { ja } from "date-fns/locale";
import styles from "./styles.module.css";

type DatepickerProps = {
  className?: string;
  dateFormat?: string;
  error?: string;
  label: string;
  isJapanese?: boolean;
  maxDate?: Date;
  native: boolean;
  placeholder?: string;
  required?: boolean;
  value: string | number;
  onChange?: (date: Date) => void;
  variant?: "default" | "refresh-2025";
};

export default function Datepicker(props: DatepickerProps) {
  const {
    className,
    dateFormat = "MM/dd/yyyy",
    error,
    label,
    isJapanese,
    maxDate,
    native = false,
    placeholder,
    required = false,
    value,
    onChange,
    variant = "default",
  } = props;

  const dateValue = value ? new Date(value) : new Date();
  const [dateObject, setDateObject] = useState(dateValue);

  const locale = useMemo(() => {
    if (isJapanese) {
      return ja;
    }

    return undefined;
  }, [isJapanese]);

  const cx = createClasses([
    className,
    styles.datepickerInput,
    {
      [styles.error]: Boolean(error),
    },
  ]);

  const wrapperClass = createClasses([
    styles.datepicker,
    {
      [styles.refresh2025]: variant === "refresh-2025",
    },
  ]);

  const popperClass = createClasses([
    styles.popper,
    {
      [styles.refresh2025]: variant === "refresh-2025",
    },
  ]);

  const calendarClass = createClasses([
    styles.calendar,
    {
      [styles.refresh2025]: variant === "refresh-2025",
    },
  ]);

  const dayClass = createClasses([
    styles.day,
    {
      [styles.refresh2025]: variant === "refresh-2025",
    },
  ]);

  const weekdayClass = createClasses([
    styles.weekday,
    {
      [styles.refresh2025]: variant === "refresh-2025",
    },
  ]);

  const updateDate = useCallback(
    (date: Date | null) => {
      if (!date) {
        return;
      }

      setDateObject(date);

      if (onChange) {
        onChange(date);
      }
    },
    [onChange]
  );

  const dayClassName = useCallback((_date: Date) => dayClass, [dayClass]);

  const weekdayClassName = useCallback(
    (_date: Date) => weekdayClass,
    [weekdayClass]
  );

  if (native) {
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!event.target.value) {
        return;
      }

      const date = new Date(event.target.value);

      if (date) {
        updateDate(date);
      }
    };

    return (
      <InputField
        className={cx}
        type="date"
        error={Boolean(error)}
        helperText={error}
        value={dateObject.toISOString().split("T")[0]}
        label={label}
        placeholder={placeholder}
        onChange={handleInputChange}
      />
    );
  }

  return (
    <DatePickerCmp
      className={cx}
      calendarClassName={calendarClass}
      popperClassName={popperClass}
      wrapperClassName={wrapperClass}
      dayClassName={dayClassName}
      weekDayClassName={weekdayClassName}
      autoComplete="off"
      locale={locale}
      selected={dateObject}
      placeholderText={placeholder}
      required={required}
      dateFormat={dateFormat}
      onChange={updateDate}
      maxDate={maxDate}
    />
  );
}
